import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Space, Tag, Modal, message, 
  Input, Select, Form, Tabs, Card, Statistic, Rate
} from 'antd';
import { 
  EditOutlined, DeleteOutlined, EyeOutlined,
  BarChartOutlined, CheckCircleOutlined, 
  CloseCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import apiService from '../services/apiService';

const { Search } = Input;
const { TabPane } = Tabs;

const Providers = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isStatsModalVisible, setIsStatsModalVisible] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providerStats, setProviderStats] = useState(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    serviceCategory: undefined,
    isActive: undefined,
    minRating: undefined,
    sortBy: 'createdAt',
    order: 'desc'
  });

  const fetchProviders = async (params = {}) => {
    setLoading(true);
    try {
      const { current, pageSize } = params;
      const { search, serviceCategory, isActive, minRating, sortBy, order } = filters;
      
      const providerParams = { 
        page: current, 
        limit: pageSize, 
        sortBy, 
        order, 
        search 
      };
      if (serviceCategory) providerParams.serviceCategory = serviceCategory;
      if (isActive !== undefined) providerParams.isActive = isActive;
      if (minRating) providerParams.minRating = minRating;

      const response = await axios.get(apiService.admin.getProviders(providerParams), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setProviders(response.data.prestataires);
      setPagination({
        ...pagination,
        total: response.data.total,
        current: response.data.currentPage
      });
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors du chargement des prestataires');
    }
    setLoading(false);
  };

  const fetchProviderStats = async (providerId) => {
    try {
      const response = await axios.get(
        apiService.admin.getProviderStats(providerId),
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setProviderStats(response.data);
      setIsStatsModalVisible(true);
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors du chargement des statistiques');
    }
  };

  useEffect(() => {
    fetchProviders({ current: 1, pageSize: 10 });
  }, [filters]);

  const handleTableChange = (newPagination, filters, sorter) => {
    const newFilters = { ...filters };
    if (sorter.field && sorter.order) {
      newFilters.sortBy = sorter.field;
      newFilters.order = sorter.order === 'descend' ? 'desc' : 'asc';
    }
    setFilters(newFilters);
    fetchProviders(newPagination);
  };

  const handleDelete = async (providerId) => {
    try {
      await axios.delete(
        apiService.admin.deleteProvider(providerId),
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      message.success('Prestataire supprimé avec succès');
      fetchProviders(pagination);
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors de la suppression');
    }
  };

  const handleToggleStatus = async (providerId, currentStatus) => {
    try {
      await axios.patch(
        apiService.admin.toggleProviderStatus(providerId),
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      message.success(`Prestataire ${currentStatus ? 'désactivé' : 'activé'} avec succès`);
      fetchProviders(pagination);
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors du changement de statut');
    }
  };

  const showModal = (provider = null) => {
    setSelectedProvider(provider);
    if (provider) {
      form.setFieldsValue({
        ...provider,
        ...provider.user
      });
    } else {
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const handleModalSubmit = async (values) => {
    try {
      await axios.put(
        apiService.admin.updateProvider(selectedProvider._id),
        values,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      message.success('Prestataire mis à jour avec succès');
      setIsModalVisible(false);
      fetchProviders(pagination);
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors de l\'enregistrement');
    }
  };

  const columns = [
    {
      title: 'Société',
      dataIndex: 'company',
      key: 'company',
      sorter: true,
      render: (company) => (
        <span className="font-medium text-neutral-900">{company}</span>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'companyType',
      key: 'companyType',
      render: (type) => (
        <Tag color="blue" className="rounded-full px-3 py-0.5">
          {type}
        </Tag>
      ),
    },
    {
      title: 'Service',
      dataIndex: 'serviceCategory',
      key: 'serviceCategory',
      filters: [
        { text: 'Traiteur', value: 'traiteur' },
        { text: 'Animation', value: 'animation' },
        { text: 'Décoration', value: 'decoration' },
        { text: 'Lieu', value: 'lieu' },
      ],
    },
    {
      title: 'Note moyenne',
      dataIndex: ['ratings', 'averageScore'],
      key: 'rating',
      render: (score) => (
        <Rate disabled defaultValue={score} allowHalf />
      ),
      sorter: true,
    },
    {
      title: 'Statut',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag 
          color={isActive ? 'success' : 'error'} 
          className="rounded-full px-3 py-0.5"
        >
          {isActive ? 'Actif' : 'Inactif'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            icon={<BarChartOutlined />}
            onClick={() => fetchProviderStats(record._id)}
            className="hover:text-primary-600 hover:border-primary-600"
          />
          <Button 
            icon={<EyeOutlined />}
            onClick={() => showModal(record)}
            className="hover:text-primary-600 hover:border-primary-600"
          />
          <Button 
            icon={<EditOutlined />}
            onClick={() => showModal(record)}
            className="hover:text-primary-600 hover:border-primary-600"
          />
          <Button
            icon={record.isActive ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
            onClick={() => handleToggleStatus(record._id, record.isActive)}
            className={record.isActive ? 'hover:text-error-600' : 'hover:text-success-600'}
          />
          <Button 
            icon={<DeleteOutlined />}
            danger
            onClick={() => {
              Modal.confirm({
                title: 'Êtes-vous sûr de vouloir supprimer ce prestataire ?',
                content: 'Cette action est irréversible.',
                okText: 'Oui',
                cancelText: 'Non',
                onOk: () => handleDelete(record._id)
              });
            }}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-6 p-6 bg-gradient-to-b from-neutral-50 to-white min-h-screen">
      <div className="flex justify-between items-center">
        <div className="space-y-1">
          <h1 className="text-heading-2 font-heading font-semibold text-neutral-900">
            Gestion des Prestataires
          </h1>
          <p className="text-neutral-500 text-body-lg">
            Gérez vos prestataires et leurs services
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-elevation-1 overflow-hidden border border-neutral-200">
        <div className="p-6 border-b border-neutral-200">
          <div className="flex flex-wrap gap-4">
            <Search
              placeholder="Rechercher un prestataire"
              onSearch={(value) => setFilters({ ...filters, search: value })}
              className="w-72"
              allowClear
            />
            <Select
              placeholder="Catégorie de service"
              className="w-48"
              allowClear
              onChange={(value) => setFilters({ ...filters, serviceCategory: value })}
            >
              <Select.Option value="traiteur">Traiteur</Select.Option>
              <Select.Option value="animation">Animation</Select.Option>
              <Select.Option value="decoration">Décoration</Select.Option>
              <Select.Option value="lieu">Lieu</Select.Option>
            </Select>
            <Select
              placeholder="Statut"
              className="w-48"
              allowClear
              onChange={(value) => setFilters({ ...filters, isActive: value })}
            >
              <Select.Option value={true}>Actif</Select.Option>
              <Select.Option value={false}>Inactif</Select.Option>
            </Select>
            <Select
              placeholder="Note minimum"
              className="w-48"
              allowClear
              onChange={(value) => setFilters({ ...filters, minRating: value })}
            >
              {[1, 2, 3, 4, 5].map(rating => (
                <Select.Option key={rating} value={rating}>
                  <Rate disabled defaultValue={rating} />
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={providers}
          rowKey="_id"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} prestataires`,
          }}
          loading={loading}
          onChange={handleTableChange}
          className="ant-table-custom"
        />
      </div>

      <Modal
        title="Détails du prestataire"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleModalSubmit}
          className="space-y-4"
        >
          {/* Informations de base */}
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              name="company"
              label="Société"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="companyType"
              label="Type d'entreprise"
              rules={[{ required: true }]}
            >
              <Select>
                <Select.Option value="SARL">SARL</Select.Option>
                <Select.Option value="SAS">SAS</Select.Option>
                <Select.Option value="EI">EI</Select.Option>
              </Select>
            </Form.Item>
          </div>

          {/* Informations de contact */}
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              name="name"
              label="Prénom"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="lastName"
              label="Nom"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </div>

          {/* Informations professionnelles */}
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              name="registrationNumber"
              label="Numéro SIRET"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="serviceCategory"
              label="Catégorie de service"
              rules={[{ required: true }]}
            >
              <Select>
                <Select.Option value="traiteur">Traiteur</Select.Option>
                <Select.Option value="animation">Animation</Select.Option>
                <Select.Option value="decoration">Décoration</Select.Option>
                <Select.Option value="lieu">Lieu</Select.Option>
              </Select>
            </Form.Item>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button onClick={() => setIsModalVisible(false)}>
              Annuler
            </Button>
            <Button type="primary" htmlType="submit">
              Enregistrer
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        title="Statistiques du prestataire"
        open={isStatsModalVisible}
        onCancel={() => setIsStatsModalVisible(false)}
        footer={null}
        width={800}
      >
        {providerStats && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <Statistic
                  title="Note moyenne globale"
                  value={providerStats.statistics.averageScore}
                  precision={2}
                  suffix={<Rate disabled defaultValue={providerStats.statistics.averageScore} allowHalf />}
                />
              </Card>
              <Card>
                <Statistic
                  title="Nombre total d'évaluations"
                  value={providerStats.statistics.totalRatings}
                />
              </Card>
            </div>

            <Card title="Moyennes par critère">
              <div className="grid grid-cols-2 gap-4">
                <Statistic
                  title="Professionnalisme"
                  value={providerStats.statistics.averageProfessionnalisme}
                  precision={2}
                />
                <Statistic
                  title="Communication"
                  value={providerStats.statistics.averageCommunication}
                  precision={2}
                />
                <Statistic
                  title="Qualité du service"
                  value={providerStats.statistics.averageQualiteService}
                  precision={2}
                />
                <Statistic
                  title="Rapport qualité/prix"
                  value={providerStats.statistics.averageRapportQualitePrix}
                  precision={2}
                />
              </div>
            </Card>

            <Card title="Dernières évaluations">
              {providerStats.recentRatings.map((rating) => (
                <div key={rating._id} className="border-b last:border-0 py-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{rating.organizer?.company}</p>
                      <p className="text-sm text-neutral-500">{rating.event?.title}</p>
                    </div>
                    <Rate disabled defaultValue={rating.score} />
                  </div>
                  {rating.comment && (
                    <p className="mt-2 text-neutral-600">{rating.comment}</p>
                  )}
                </div>
              ))}
            </Card>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Providers; 